import { useCallback, useEffect, useRef, useState } from 'react';
import { MarkdownDisplay } from './shared/MarkdownDisplay';
import {
  fetchCurrentDocument,
  PolicyDocument,
  PolicyDocumentType,
} from '../services/policyDocuments';

interface PolicyDocumentContentProps {
  type: PolicyDocumentType;
  /**
   * Fired with the fetched document each time the fetch succeeds, so a parent
   * can surface its version / effective date in its own chrome — the public
   * page shell header (task 441) and the ConsentGate checklist (task 648).
   */
  onLoaded?: (document: PolicyDocument) => void;
}

/**
 * The document content starts with an H1 title line (the AMS content
 * contract). Both call sites render their own title chrome around this body,
 * so the markdown's leading H1 is stripped to avoid a duplicated heading.
 */
function stripLeadingH1(markdown: string): string {
  return markdown.replace(/^\s*#\s+[^\n]*\n?/, '');
}

type FetchState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; document: PolicyDocument };

/**
 * Live policy-document body, shared by the public policy pages (task 441,
 * `PolicyDocumentPage`) and the ConsentGate reader (task 648, reworked for the
 * explicit-agree dialog in task 666).
 *
 * Fetches the current PUBLISHED policy document from the innovate-service
 * public proxy via `fetchCurrentDocument` and renders its markdown through the
 * shared sanitized `MarkdownDisplay` pipeline. Owns its own loading / error /
 * retry state; a hard fetch failure renders a retry button and never throws,
 * so a caller can rely on it to fail closed behind a gate.
 *
 * Uses local state rather than React Query: the public branch of App.tsx
 * mounts no QueryClientProvider, and the gate reader wants the same
 * self-contained fetch there.
 */
export default function PolicyDocumentContent({ type, onLoaded }: PolicyDocumentContentProps) {
  const [state, setState] = useState<FetchState>({ status: 'loading' });

  // Hold the latest onLoaded in a ref so `load` depends only on `type` — an
  // inline callback from the parent must not re-create `load` and re-fetch.
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const document = await fetchCurrentDocument(type);
      setState({ status: 'ready', document });
      onLoadedRef.current?.(document);
    } catch {
      setState({ status: 'error' });
    }
  }, [type]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === 'loading') {
    return (
      <div data-testid="policy-page-loading" className="animate-pulse space-y-4">
        <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-5/6 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div
        data-testid="policy-page-error"
        className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-6 text-center"
      >
        <p className="text-gray-700 dark:text-gray-300">
          We could not load the policy text right now.
        </p>
        <button
          type="button"
          data-testid="policy-page-retry"
          onClick={() => void load()}
          className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Try again
        </button>
      </div>
    );
  }

  return <MarkdownDisplay content={stripLeadingH1(state.document.content)} bare />;
}
